<!--
  SuperAdminDashboard -- the bare-`/admin` landing for Super Admins.

  Wired to POST /api/superadmin/dashboard-summary via the
  useSuperAdminSummaryStore (fetched by SuperAdminLayout on mount).
  This component reads from the store; it does NOT make its own
  API call.

  Data binding:
    store.summary.dashboard  -> headline KPI tiles (MRR, distributors,
                                stores, trials)
    store.summary.risk_watch -> risk-row queue (first 3 rows shown)
    store.summary.money      -> gross revenue KPI in the revenue tile
                                (replaces the 6-bar chart -- CUT for
                                beta; no trailing-MRR series endpoint
                                exists yet; the KPI tile keeps the
                                "Revenue" section visible and useful)

  Onboarding funnel is still stubbed (pipeline data needs a dedicated
  onboarding-funnel endpoint, not yet built). The funnel section is
  hidden when no store.summary is available.

  ASCII-only.
-->
<template>
    <div class="sa-dash" data-testid="sa-dashboard">
        <header class="sa-dash__header">
            <div class="sa-dash__eyebrow">ThreadFlow Admin</div>
            <h1 class="sa-dash__title">Overview</h1>
            <p class="sa-dash__lede">
                Platform health at a glance -- revenue, distributor activity,
                and what needs attention today.
            </p>
        </header>

        <!-- Loading / error states -->
        <p
            v-if="summaryStore.loading"
            class="sa-state"
            data-testid="sa-dashboard-loading"
        >
            Loading...
        </p>
        <p
            v-else-if="summaryStore.error"
            class="sa-state sa-state--error"
            data-testid="sa-dashboard-error"
        >
            Could not load dashboard data.
        </p>

        <div v-else class="sa-dash__body">
            <!-- Headline stats from summary.dashboard -->
            <div class="sa-dash__stats">
                <div
                    v-for="stat in statTiles"
                    :key="stat.label"
                    class="sa-stat"
                    data-testid="sa-stat"
                >
                    <div class="sa-stat__label">{{ stat.label }}</div>
                    <div class="sa-stat__value" :data-testid="stat.valueTestid">{{ stat.value }}</div>
                    <div class="sa-stat__delta-row">
                        <span class="sa-stat__sub">{{ stat.sub }}</span>
                    </div>
                </div>
            </div>

            <!-- Needs attention (risk_watch from summary) -->
            <section
                v-if="riskRows.length > 0"
                class="sa-card"
                data-testid="sa-attention"
            >
                <div class="sa-card__head">
                    <h2 class="sa-card__title">Needs attention</h2>
                    <p class="sa-card__sub">
                        Distributors flagged by Risk Watch.
                    </p>
                </div>
                <div class="sa-attention__rows">
                    <div
                        v-for="row in riskRows"
                        :key="row.id || row.distributor || row.label"
                        class="sa-risk-row"
                        data-testid="sa-risk-row"
                    >
                        <span
                            class="sa-risk-row__icon"
                            :class="
                                row.severity === 'high'
                                    ? 'is-high'
                                    : 'is-med'
                            "
                        >
                            <span class="material-icons">{{
                                row.icon || "warning"
                            }}</span>
                        </span>
                        <div class="sa-risk-row__body">
                            <div class="sa-risk-row__dist">
                                <DistributorLink
                                    :distributor-id="row.distributorId"
                                    :name="row.distributor"
                                />
                            </div>
                            <div
                                class="sa-risk-row__label"
                                :class="
                                    row.severity === 'high'
                                        ? 'is-high'
                                        : 'is-med'
                                "
                            >
                                {{ row.label }}
                            </div>
                        </div>
                        <!--
                          U5: per-distributor CTA cluster. "View" navigates to
                          the SA distributor edit page (real, works today).
                          "Resolve" is the honest placeholder for the design's
                          one-click remediation labels (Retry/contact, Resend,
                          ...) -- it opens a coming-soon dialog rather than
                          pretending to remediate. Both are styled as the
                          design's rounded pill, right-aligned after the
                          flex:1 body.
                        -->
                        <div class="sa-risk-row__cta-group">
                            <router-link
                                :to="`/admin/distributors/${row.distributorId}/edit`"
                                class="sa-risk-pill"
                                data-testid="sa-risk-row__cta"
                            >
                                View
                            </router-link>
                            <button
                                type="button"
                                class="sa-risk-pill sa-risk-pill--muted"
                                data-testid="sa-risk-row__resolve"
                                @click="openRemediationDialog"
                            >
                                Resolve
                            </button>
                        </div>
                    </div>
                </div>
                <router-link
                    to="/admin/risk-watch"
                    class="sa-card__link"
                    data-testid="sa-risk-viewall"
                >
                    View all on Risk Watch
                    <span class="material-icons">chevron_right</span>
                </router-link>
            </section>

            <!-- Onboarding + Revenue -->
            <div class="sa-dash__grid2">
                <!-- Onboarding pipeline (derived from milestone data) -->
                <section class="sa-card" data-testid="sa-onboarding">
                    <div class="sa-card__head">
                        <h2 class="sa-card__title">Onboarding pipeline</h2>
                        <p class="sa-card__sub">Distributors mid-activation.</p>
                    </div>
                    <div
                        v-for="(stage, i) in onboardingStages"
                        :key="stage.stage"
                        class="sa-funnel__row"
                        :class="{ 'is-first': i === 0 }"
                        data-testid="sa-onboarding-stage"
                    >
                        <div class="sa-funnel__stage">{{ stage.stage }}</div>
                        <div class="sa-funnel__track">
                            <div
                                class="sa-funnel__fill"
                                :style="{
                                    width: `${(stage.n / onboardingMax) * 100}%`,
                                }"
                            ></div>
                        </div>
                        <div class="sa-funnel__n">{{ stage.n }}</div>
                    </div>
                </section>

                <!-- Revenue: single KPI tile (6-bar chart cut for beta) -->
                <section class="sa-card" data-testid="sa-revenue">
                    <div class="sa-card__head">
                        <h2 class="sa-card__title">Revenue (30d)</h2>
                        <p class="sa-card__sub">Gross revenue, trailing 30 days.</p>
                    </div>
                    <div
                        class="sa-revenue-kpi"
                        data-testid="sa-revenue-kpi"
                    >
                        {{ grossRevenue30d }}
                    </div>
                </section>
            </div>

            <!--
              U5: honest coming-soon dialog for the risk-row "Resolve"
              affordance. One-click remediation (retry payment, resend MSA,
              extend trial) is not yet wired to a backend action, so this
              states that plainly and points the SA at the working "View"
              path. Hand-rolled overlay to match the page's token-based
              styling and stay testable without the Vuetify plugin.
            -->
            <div
                v-if="remediationDialogOpen"
                class="sa-modal__scrim"
                data-testid="sa-risk-remediation-dialog"
                @click.self="closeRemediationDialog"
            >
                <div
                    class="sa-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="sa-risk-remediation-title"
                >
                    <h3
                        id="sa-risk-remediation-title"
                        class="sa-modal__title"
                    >
                        One-click remediation is coming soon
                    </h3>
                    <p class="sa-modal__body">
                        One-click remediation is not yet available. Use
                        "View" to open the distributor and act manually.
                    </p>
                    <div class="sa-modal__actions">
                        <button
                            type="button"
                            class="sa-risk-pill"
                            data-testid="sa-risk-remediation-ok"
                            @click="closeRemediationDialog"
                        >
                            OK
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DistributorLink from "@/components/superadmin/DistributorLink.vue";
import { useSuperAdminSummaryStore } from "@/stores/superAdminSummary";

function formatCents(cents) {
    if (cents === null || cents === undefined) return "--";
    return `$${(cents / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

// Map a risk event_type to a material-icons glyph for the attention row.
// Falls back to "warning" for an unknown type so a new backend trigger
// never renders blank.
const RISK_ICONS = {
    payment_failed: "credit_card_off",
    inactive_30d: "person_off",
    volume_drop: "trending_down",
    cancellation_request: "cancel",
};
function riskIconFor(eventType) {
    return RISK_ICONS[eventType] || "warning";
}

export default {
    components: { DistributorLink },
    name: "SuperAdminDashboard",
    setup() {
        const summaryStore = useSuperAdminSummaryStore();
        return { summaryStore };
    },
    data() {
        return {
            // U5: controls the honest "remediation coming soon" dialog opened
            // from a risk row's Resolve pill.
            remediationDialogOpen: false,
        };
    },
    methods: {
        openRemediationDialog() {
            this.remediationDialogOpen = true;
        },
        closeRemediationDialog() {
            this.remediationDialogOpen = false;
        },
    },
    computed: {
        // ---- metrics.dashboard -> headline stat tiles --------------------
        // metrics is set by the store after POST /superadmin/dashboard-summary.
        dashboardMetrics() {
            return (
                this.summaryStore.metrics &&
                this.summaryStore.metrics.dashboard
            ) || null;
        },
        statTiles() {
            const d = this.dashboardMetrics;
            if (!d) return [];
            return [
                {
                    label: "MRR",
                    value: formatCents(d.mrr_cents),
                    valueTestid: "sa-stat-mrr",
                    sub: "",
                },
                {
                    label: "Active distributors",
                    value: d.active_distributors != null
                        ? String(d.active_distributors)
                        : "--",
                    sub: d.new_distributors_this_month != null
                        ? `+${d.new_distributors_this_month} this month`
                        : "",
                },
                {
                    label: "Active stores",
                    value: d.active_stores != null
                        ? String(d.active_stores)
                        : "--",
                    sub: d.new_stores_this_month != null
                        ? `+${d.new_stores_this_month} this month`
                        : "",
                },
                {
                    label: "Trials ending <=7d",
                    value: d.trials_ending_7d != null
                        ? String(d.trials_ending_7d)
                        : "--",
                    sub: "",
                },
            ];
        },

        // ---- summary.risk_watch.rows -> attention queue (max 3 rows) ----
        // B1: the API's risk_watch summary now carries a `rows` array of the
        // top open risk events (shape: {id, distributor_id, distributor_name,
        // event_type, severity, occurred_at, summary}). Map each to the
        // display shape the template renders {id, distributorId, severity,
        // icon, distributor, label}. The backend already caps this at 3.
        //
        // U5: id + distributorId are carried through so each row can render a
        // per-distributor CTA pill -- a "View" router-link to the SA
        // distributor edit page (mirrors RiskWatch.vue).
        riskRows() {
            const rw =
                this.summaryStore.summary &&
                this.summaryStore.summary.risk_watch;
            const rows = (rw && rw.rows) || [];
            return rows.map((r) => ({
                id: r.id,
                distributorId: r.distributor_id,
                severity: r.severity,
                icon: riskIconFor(r.event_type),
                distributor: r.distributor_name,
                label: r.summary,
            }));
        },

        // ---- metrics.dashboard -> gross revenue KPI ---------------------
        grossRevenue30d() {
            const d = this.dashboardMetrics;
            if (!d) return "--";
            return formatCents(d.gross_revenue_30d_cents);
        },

        // ---- metrics.onboarding_funnel -> funnel stages -----------------
        onboardingStages() {
            return (
                this.summaryStore.metrics &&
                this.summaryStore.metrics.onboarding_funnel
            ) || [];
        },

        // Onboarding funnel bar scaling.
        onboardingMax() {
            if (!this.onboardingStages.length) return 1;
            return Math.max(...this.onboardingStages.map((s) => s.n), 1);
        },
    },
};
</script>

<style scoped>
.sa-dash {
    max-width: 1100px;
}
.sa-dash__header {
    margin-bottom: 22px;
}
.sa-dash__eyebrow {
    font-size: 11px;
    color: var(--tf-fg-4);
    font-weight: 500;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    margin-bottom: 6px;
}
.sa-dash__title {
    font-size: 28px;
    font-weight: 500;
    color: var(--tf-fg-1);
    font-family: var(--tf-font-display), sans-serif;
    letter-spacing: -0.3px;
    margin: 0 0 6px;
}
.sa-dash__lede {
    font-size: 14px;
    color: var(--tf-fg-3);
    margin: 0;
    max-width: 640px;
}
.sa-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-state--error {
    color: #c53030;
}

/* Stats grid */
.sa-dash__stats {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 14px;
    margin-bottom: 18px;
}
.sa-stat {
    background: #fff;
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-md);
    padding: 18px 20px;
}
.sa-stat__label {
    font-size: 11px;
    color: var(--tf-fg-4);
    font-weight: 500;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    margin-bottom: 6px;
}
.sa-stat__value {
    font-size: 26px;
    font-weight: 500;
    color: var(--tf-fg-1);
    font-family: var(--tf-font-display), sans-serif;
    letter-spacing: -0.3px;
}
.sa-stat__delta-row {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 4px;
}
.sa-stat__sub {
    font-size: 11px;
    color: var(--tf-fg-4);
}

/* Card */
.sa-card {
    background: #fff;
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-md);
    padding: 20px;
    margin-bottom: 14px;
}
.sa-card__head {
    margin-bottom: 14px;
}
.sa-card__title {
    font-size: 16px;
    font-weight: 500;
    color: var(--tf-fg-1);
    font-family: var(--tf-font-display), sans-serif;
    margin: 0 0 2px;
}
.sa-card__sub {
    font-size: 13px;
    color: var(--tf-fg-4);
    margin: 0;
}
.sa-card__link {
    color: var(--tf-primary);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    margin-top: 12px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    text-decoration: none;
}
.sa-card__link .material-icons {
    font-size: 16px;
}

/* Risk rows */
.sa-attention__rows {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.sa-risk-row {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 14px;
    background: #fafbfc;
    border: 1px solid var(--tf-border);
    border-radius: 10px;
}
.sa-risk-row__icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
.sa-risk-row__icon.is-high {
    background: #fdecec;
}
.sa-risk-row__icon.is-med {
    background: #fff8e1;
}
.sa-risk-row__icon .material-icons {
    font-size: 18px;
}
.sa-risk-row__icon.is-high .material-icons {
    color: var(--tf-red);
}
.sa-risk-row__icon.is-med .material-icons {
    color: #b26a00;
}
.sa-risk-row__body {
    flex: 1;
    min-width: 0;
}
.sa-risk-row__dist {
    font-size: 14px;
    color: var(--tf-fg-1);
    font-weight: 500;
}
.sa-risk-row__label {
    font-size: 12px;
    margin-top: 2px;
}
.sa-risk-row__label.is-high {
    color: var(--tf-red);
}
.sa-risk-row__label.is-med {
    color: #b26a00;
}

/* Risk-row CTA pills (U5) -- design: white bg, 1px border, fully rounded,
   6px 14px padding, 12px text, right-aligned after the flex:1 body. */
.sa-risk-row__cta-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}
.sa-risk-pill {
    background: #fff;
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-pill);
    padding: 6px 14px;
    font-size: 12px;
    color: var(--tf-fg-2);
    white-space: nowrap;
    text-decoration: none;
    cursor: pointer;
    line-height: 1.2;
    font-family: inherit;
}
.sa-risk-pill:hover {
    border-color: var(--tf-border-strong);
    background: #fafbfc;
}
.sa-risk-pill--muted {
    color: var(--tf-fg-4);
}

/* Coming-soon dialog (U5) */
.sa-modal__scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.32);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
}
.sa-modal {
    background: #fff;
    border: 1px solid var(--tf-border);
    border-radius: var(--tf-radius-md);
    padding: 22px 24px;
    max-width: 420px;
    width: 100%;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.16);
}
.sa-modal__title {
    font-size: 16px;
    font-weight: 500;
    color: var(--tf-fg-1);
    font-family: var(--tf-font-display), sans-serif;
    margin: 0 0 8px;
}
.sa-modal__body {
    font-size: 13px;
    color: var(--tf-fg-3);
    margin: 0 0 18px;
    line-height: 1.5;
}
.sa-modal__actions {
    display: flex;
    justify-content: flex-end;
}

/* Two-col grid */
.sa-dash__grid2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
}

/* Onboarding funnel */
.sa-funnel__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    border-top: 1px solid var(--tf-border);
}
.sa-funnel__row.is-first {
    border-top: none;
}
.sa-funnel__stage {
    flex: 1;
    font-size: 13px;
    color: var(--tf-fg-2);
}
.sa-funnel__track {
    width: 120px;
    height: 8px;
    border-radius: var(--tf-radius-pill);
    background: #eef0f3;
    overflow: hidden;
}
.sa-funnel__fill {
    height: 100%;
    background: var(--tf-primary);
}
.sa-funnel__n {
    width: 24px;
    text-align: right;
    font-size: 13px;
    color: var(--tf-fg-1);
    font-weight: 500;
    font-family: var(--tf-font-mono), monospace;
}

/* Revenue KPI tile (chart cut for beta) */
.sa-revenue-kpi {
    font-size: 32px;
    font-weight: 600;
    color: var(--tf-fg-1);
    font-family: var(--tf-font-display), sans-serif;
    letter-spacing: -0.5px;
    padding: 12px 0 4px;
}

@media (max-width: 860px) {
    .sa-dash__stats {
        grid-template-columns: 1fr 1fr;
    }
    .sa-dash__grid2 {
        grid-template-columns: 1fr;
    }
}
</style>
